import { useEffect, useId, useState } from "react";

import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";

import { useFieldContext } from "./field-context";
import { useFieldError } from "./field-errors";

/** Empty/blank → null; otherwise Number(value), or null when unparseable. Mirrors offer-form's parseOptionalNumber. */
function parseOptionalNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

export function NumberField({
  label,
  placeholder,
  required,
  helpText,
  disabled,
  min,
  step,
}: {
  label?: string;
  placeholder?: string;
  required?: boolean;
  helpText?: string;
  disabled?: boolean;
  min?: number;
  step?: number;
}) {
  const field = useFieldContext<number | null | undefined>();
  const error = useFieldError(field.name);
  const id = useId();

  // Buffer the raw typed string. A controlled number input that re-derives its
  // value from the parsed number strips in-progress decimals ("49." -> "49",
  // blocking "49.99"). Keeping the draft preserves exactly what the user typed.
  const [draft, setDraft] = useState(() =>
    field.state.value == null ? "" : String(field.state.value),
  );

  // Re-sync the draft only when the form value changes from outside (load,
  // reset, navigation) — i.e. when it diverges from what the draft represents.
  // When they agree (parse(draft) === value), leave the draft so "49." survives.
  useEffect(() => {
    const value = field.state.value ?? null;
    if (parseOptionalNumber(draft) !== value) {
      setDraft(value == null ? "" : String(value));
    }
    // `draft` is intentionally read, not a trigger; we react to external changes.
  }, [field.state.value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormField
      inputId={id}
      label={label}
      required={required}
      helpText={helpText}
      error={error}
    >
      <Input
        id={id}
        type="number"
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        step={step}
        value={draft}
        onValueChange={(v) => {
          setDraft(v);
          field.handleChange(parseOptionalNumber(v));
        }}
        onBlur={field.handleBlur}
      />
    </FormField>
  );
}
